import {
  ClipboardCheck,
  RefreshCcw,
  TriangleAlert,
  Undo2,
  type LucideIcon,
} from "lucide-react";
import { formatDashboardCount } from "@/features/dashboard/metricFormatters";
import type { DashboardQualityOutcomes as QualityOutcomes } from "@/features/dashboard/types";
import "./DashboardLedger.css";

const QUALITY_CSS = `
  .dashboard-ledger-quality {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    min-width: 0;
    border-block: 1px solid var(--ledger-border);
    background: var(--ledger-surface);
  }
  .dashboard-ledger-quality__item {
    width: 100%;
    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    align-items: center;
    gap: 5px 8px;
    min-width: 0;
    padding: 12px 15px;
    border-right: 1px solid var(--ledger-border);
    background: transparent;
    color: inherit;
    font: inherit;
    text-align: left;
  }
  button.dashboard-ledger-quality__item { cursor: pointer; }
  button.dashboard-ledger-quality__item:hover { background: var(--ledger-surface-muted); }
  button.dashboard-ledger-quality__item:focus-visible {
    position: relative;
    z-index: 1;
    outline: 2px solid var(--ledger-accent);
    outline-offset: -2px;
  }
  .dashboard-ledger-quality__item:last-child { border-right: 0; }
  .dashboard-ledger-quality__icon {
    grid-row: 1 / span 2;
    display: grid;
    width: 30px;
    height: 30px;
    place-items: center;
    border: 1px solid currentColor;
    border-radius: 50%;
  }
  .dashboard-ledger-quality__item[data-tone="neutral"] { color: var(--dashboard-semantic-neutral, var(--ledger-muted)); }
  .dashboard-ledger-quality__item[data-tone="warning"] { color: var(--dashboard-semantic-warning, #9a6400); }
  .dashboard-ledger-quality__item[data-tone="negative"] { color: var(--dashboard-semantic-negative, var(--ledger-error)); }
  .dashboard-ledger-quality__label {
    min-width: 0;
    color: var(--ledger-muted);
    font-size: 0.75rem;
    font-weight: 700;
    line-height: 1.2;
  }
  .dashboard-ledger-quality__value {
    color: var(--ledger-text);
    font-family: "Barlow Condensed", "Arial Narrow", sans-serif;
    font-size: 1.3rem;
    font-variant-numeric: tabular-nums;
    line-height: 1;
    white-space: nowrap;
  }
  .dashboard-ledger-quality__value[data-length="xlong"] {
    font-size: 1rem;
    letter-spacing: -0.02em;
  }
  @media (max-width: 640px) {
    .dashboard-ledger-quality { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    .dashboard-ledger-quality__item:nth-child(2n) { border-right: 0; }
    .dashboard-ledger-quality__item:nth-child(n + 3) { border-top: 1px solid var(--ledger-border); }
    .dashboard-ledger-quality__item { padding: 11px 12px; }
    .dashboard-ledger-quality__label { font-size: 0.8125rem; }
  }
  @media (max-width: 400px) {
    .dashboard-ledger-quality { grid-template-columns: minmax(0, 1fr); }
    .dashboard-ledger-quality__item { border-right: 0; }
    .dashboard-ledger-quality__item:nth-child(n + 2) {
      border-top: 1px solid var(--ledger-border);
    }
  }
`;

export interface DashboardQualityOutcomesProps {
  outcomes: QualityOutcomes;
  ariaLabel?: string;
  onSelect?: (outcome: keyof QualityOutcomes) => void;
}

interface OutcomePresentation {
  key: keyof QualityOutcomes;
  label: string;
  tone: "neutral" | "warning" | "negative";
  Icon: LucideIcon;
}

const presentations: readonly OutcomePresentation[] = [
  {
    key: "decision_count",
    label: "Decisões registradas",
    tone: "neutral",
    Icon: ClipboardCheck,
  },
  {
    key: "substituted_count",
    label: "Substituições",
    tone: "warning",
    Icon: RefreshCcw,
  },
  {
    key: "returned_count",
    label: "Devoluções",
    tone: "negative",
    Icon: Undo2,
  },
  {
    key: "divergence_count",
    label: "Divergências",
    tone: "negative",
    Icon: TriangleAlert,
  },
];

export function DashboardQualityOutcomes({
  outcomes,
  ariaLabel = "Qualidade nas cargas",
  onSelect,
}: DashboardQualityOutcomesProps) {
  return (
    <>
      <style>{QUALITY_CSS}</style>
      <section
        className="dashboard-ledger dashboard-ledger-quality"
        aria-label={ariaLabel}
      >
        {presentations.map(({ key, label, tone, Icon }) => {
          const count = formatDashboardCount(outcomes[key]);
          const content = (
            <>
              <span
                className="dashboard-ledger-quality__icon"
                aria-hidden="true"
              >
                <Icon aria-hidden="true" size={16} strokeWidth={2.1} />
              </span>
              <span className="dashboard-ledger-quality__label">{label}</span>
              <strong
                className="dashboard-ledger-quality__value dashboard-ledger-number"
                data-length={count.full.length > 12 ? "xlong" : "regular"}
              >
                {count.full}
              </strong>
            </>
          );
          const shared = {
            className: "dashboard-ledger-quality__item",
            "data-tone": tone,
            "aria-label": `${label}: ${count.accessibleLabel}`,
          } as const;
          return onSelect ? (
            <button
              {...shared}
              type="button"
              key={key}
              onClick={() => onSelect(key)}
            >
              {content}
            </button>
          ) : (
            <div {...shared} key={key}>
              {content}
            </div>
          );
        })}
      </section>
    </>
  );
}
